import { getCurrentScope } from '@sentry/nuxt';
import { getCurrentInstance } from 'vue';
import { useRoute } from '#vue-router';
import type { RouteLocationNormalizedLoaded } from '#vue-router';
import type { BaseStoreConfig, BaseStoreKeys } from '@/utils/baseStoreConfig';
import type { NuxtApp } from '#app';
import type { ProductRecall } from '~/types/misc';

export type MainState = {
  abTests: {
    [p: string]: any;
    searchhub: boolean;
    tests: null | string;
    UXR72DeliveryCost: boolean;
  };
  accountNodes: any[];
  baseStore?: BaseStoreKeys;
  baseStoreConfig: Partial<BaseStoreConfig>;
  billingCountries: Array<Record<string, any>>;
  configuration: any;
  contactReasonExist: boolean;
  contentTiles: any[];
  defaultCountry: string;
  devicePlatform: string | null;
  friendsBannerClosed: boolean;
  smartBannerClosed: boolean;
  campaignHeaderClosed: boolean;
  emergencyHeaderClosed: boolean;

  error?: {
    code: number;
    image?: string;
    title: string;
    text: string;
    button?: string;
    redirectUrl?: string;
    closable?: boolean;
  };

  errorPage: any | null;
  features: any;
  flexibleFooter: any | null;
  flexibleHeader: any | null;
  flexibleLayoutType: any | null;
  footer: any | null;
  header: any | null;
  headerHeight: number;
  optionalHeadersHeight: number;
  hrefLang: any | null;
  iframeModalUrl: string | null;
  isMultilang: boolean;
  language: string;

  /**
   * Global page loading state.
   */
  isLoading: boolean;
  firstLoad: boolean;

  mboxes: any[];

  loadedPage: any;
  pageTemplate: string;
  pdpBreadcrumbs: {
    nodes: any[];
  };
  pdpSearchQuery: string;
  preservedEmail: string;
  progress: {
    activeStep: number;
    steps: number;
  };
  salesDisabled: boolean;
  searchedStores: any | null;
  searchTerm: any | null;
  sectionSmileHeight: number;
  shippingCountries: Array<Record<string, any>>;
  voucherAdded?: {
    status: boolean | 'loading';
    error?: { type: string };
  };

  // Nuxt3 additions below
  isAppview: boolean;
  isMobileView: boolean;
  isRootFocus: boolean;
  contactReasonValidate?: {
    resolve: typeof Promise.resolve;
    reject: typeof Promise.reject;
  };

  shouldClearCookies?: Array<string>;

  productRecalls?: Array<ProductRecall>;

  lastRoutePath: string;
};

function useGetFeature(state: any, name: string) {
  if (state.features?.featureToggles) {
    return !!state.features.featureToggles.find((feature: any) => feature.code === name && feature.active);
  }
  return false;
}

export const useMainStore = defineStore('main', {
  state: (): MainState => ({
    abTests: {
      searchhub: false,
      tests: null,
      UXR72DeliveryCost: false,
    },
    accountNodes: [],
    baseStore: undefined,
    baseStoreConfig: {} as BaseStoreConfig,
    billingCountries: [],
    configuration: null,
    contactReasonExist: false,
    contentTiles: [],
    defaultCountry: 'DE',
    devicePlatform: null,

    // Header state
    friendsBannerClosed: false,
    smartBannerClosed: false,
    campaignHeaderClosed: false,
    emergencyHeaderClosed: false,

    error: null,
    errorPage: null,
    features: null,
    flexibleFooter: null,
    flexibleHeader: null,
    flexibleLayoutType: null,
    footer: null,
    header: {},
    headerHeight: 110,
    optionalHeadersHeight: 0,
    hrefLang: null,
    iframeModalUrl: null,
    isMultilang: false,
    language: '',

    isLoading: false,
    firstLoad: true,

    mboxes: [],
    pageTemplate: 'default',
    pdpBreadcrumbs: {
      nodes: [],
    },
    pdpSearchQuery: '',
    preservedEmail: '',
    progress: {
      activeStep: 0,
      steps: 0,
    },
    salesDisabled: false,
    searchedStores: null,
    searchTerm: null,
    sectionSmileHeight: 0,
    shippingCountries: [],
    voucherAdded: undefined,

    // Nuxt3 additions below
    isAppview: false,
    isMobileView: false,
    loadedPage: null,
    isRootFocus: false,
    contactReasonValidate: undefined,

    shouldClearCookies: undefined,

    productRecalls: [],

    lastRoutePath: '',
  }),
  getters: {
    getBaseStoreConfig(state) {
      return state.baseStoreConfig;
    },

    getIsPaybackInternationalApi(state) {
      return state.configuration?.paybackApi === 'INT';
    },

    getMinimumOrderValue(state) {
      return state.configuration?.minimumOrderValue;
    },

    getConfigPaymentModes(state) {
      return state.configuration?.paymentModes;
    },

    getPaybackAuthLogoutUrl(state) {
      return state.configuration?.paybackAuthLogoutUrl;
    },

    getShippingFee(state) {
      return state.configuration?.shippingFee;
    },

    getPaybackEnabled(state) {
      return state.configuration?.paybackEnabled;
    },

    getPaybackRedemptionEnabled(state) {
      return state.configuration?.paybackEnabled && state.configuration?.paybackRedemptionEnabled;
    },

    getPaybackRedemptionEnabledForApp(state) {
      return state.configuration?.paybackEnabled && state.configuration?.paybackRedemptionEnabledForApp;
    },

    getUrlPrefix(state) {
      return state.language && state.configuration?.language?.available?.length > 1
        ? `/${state.configuration?.language.available.find((lang: any) => lang.locale === state.language).isocode}`
        : null;
    },

    getShippingCountry: (state) => (countryIsocode: string) => {
      const isoLowerCase = countryIsocode.toLowerCase();
      return state.shippingCountries.find((shippingCountry) => shippingCountry.isocode.toLowerCase() === isoLowerCase);
    },

    getIsShippingCountry() {
      return (countryIsocode: any) => !!this.getShippingCountry(countryIsocode);
    },

    getBillingCountry(state) {
      return (countryIsocode: string) => {
        const isoLowerCase = countryIsocode.toLowerCase();
        return state.billingCountries.find((billingCountry) => billingCountry.isocode.toLowerCase() === isoLowerCase);
      };
    },

    getIsMultilang(state) {
      return state.configuration && state.configuration?.language && state.configuration?.language.available.length > 1;
    },

    getAvailableLanguages(state) {
      return state.configuration?.language?.available;
    },

    getMbox: (state: MainState) => (id: any) =>
      state.mboxes?.find((mbox) => {
        return mbox.id === id;
      }),

    getFreeShippingThreshold(state) {
      return state.configuration?.freeShippingThreshold;
    },

    getCdnHostUrl(state) {
      return state.configuration?.cdnHostUrl;
    },

    getFeature(state) {
      return (name: string) => useGetFeature(state, name);
    },

    getAccountDeletionEnabled(state) {
      return useGetFeature(state, 'accountDeletionEnabled');
    },

    getSalonPriceItemsNewEnabled(state) {
      return useGetFeature(state, 'usingNewSalonPriceItemsEnabled');
    },

    getLegacyContact(state): any {
      return useGetFeature(state, 'legacyContact');
    },

    getEcomEnabledApp(state): any {
      return useGetFeature(state, 'ecomEnabledApp');
    },

    getCuralateGalleryEnabled(state): any {
      return useGetFeature(state, 'curalateGalleryEnabled');
    },

    getCuralateGalleryLazyLoad(state): any {
      return useGetFeature(state, 'curalateGalleryLazyLoad');
    },

    getFriendsBonus(state): any {
      return useGetFeature(state, 'friendsBonus');
    },

    getFriendsUnsubscribeEnabled(state): any {
      return useGetFeature(state, 'friendsUnsubscribeEnabled');
    },

    getSalonBookingEnabled(state): any {
      return useGetFeature(state, 'salonBookingEnabled');
    },

    getNewPaybackIntegration(state: MainState) {
      return state.configuration.paybackApi === 'PBI';
    },

    getAllComponentsInAppEnabled(state): any {
      return useGetFeature(state, 'allComponentsInAppEnabled');
    },

    getEmailChangeEnabled(state): any {
      return useGetFeature(state, 'emailChangeEnabled');
    },

    getLoginRecaptchaEnabled(state): any {
      return useGetFeature(state, 'loginRecaptchaEnabled');
    },

    getRegistrationRecaptchaEnabled(state): any {
      return useGetFeature(state, 'registrationRecaptchaEnabled');
    },

    getNewsletterRecaptchaEnabled(state): any {
      return useGetFeature(state, 'newsletterRecaptchaEnabled');
    },

    getContactRecaptchaEnabled(state): any {
      return useGetFeature(state, 'contactRecaptchaEnabled');
    },

    getVotingRecaptchaEnabled(state): any {
      return useGetFeature(state, 'votingRecaptchaEnabled');
    },

    getForgotPasswordRecaptchaEnabled(state): any {
      return useGetFeature(state, 'forgotPasswordRecaptchaEnabled');
    },

    getEmailChangeRecaptchaEnabled(state): any {
      return useGetFeature(state, 'emailChangeRecaptchaEnabled');
    },

    getRaffleRecaptchaEnabled(state): any {
      return useGetFeature(state, 'raffleRecaptchaEnabled');
    },

    getPaypalReferenceTransactionsEnabled(state): any {
      return useGetFeature(state, 'paypalReferenceTransactions');
    },

    getPetmojiEnabled(state): any {
      return useGetFeature(state, 'petmojiEnabled');
    },

    getSovendusEnabled(state): any {
      return useGetFeature(state, 'sovendusEnabled');
    },

    getShowFreeShipping(state): any {
      return useGetFeature(state, 'showFreeShipping');
    },

    getShowOPPSpromotionToast(state) {
      return useGetFeature(state, 'showOPPSpromotionToast');
    },

    getSovidoEnabled(state): any {
      return useGetFeature(state, 'sovidoEnabled');
    },

    getCcaSubscriptionApiEnabled(state): any {
      return useGetFeature(state, 'ccaSubscriptionApiEnabled');
    },

    showOPPSpromotionToast(state): any {
      return useGetFeature(state, 'showOPPSpromotionToast');
    },

    getClickAndCollectEnabled(state): any {
      return useGetFeature(state, 'clickAndCollectEnabled');
    },

    getPickUpStationEnabled(state): any {
      return useGetFeature(state, 'pickUpStationEnabled');
    },

    clickAndCollectRolloutStoresBehaviourEnabled(state): any {
      return useGetFeature(state, 'clickAndCollectRolloutStoresBehaviourEnabled');
    },

    getShowPosAvailability(state): any {
      return useGetFeature(state, 'showPosAvailability');
    },

    getFilteredAccountNodes(state: MainState) {
      if (!state.accountNodes?.length) {
        return [];
      }

      const userStore = useUserStore();
      const isShelterAccount = (userStore.user as any)?.isShelterAccount;
      return state.accountNodes.filter((node: any) => {
        const isHiddenForShelters = isShelterAccount && node.categories?.includes('shelter-only-menu-hidden');
        const isHiddenForNotShelter = !isShelterAccount && node.categories?.includes('shelter-only-menu-relevant');
        return !(isHiddenForShelters || isHiddenForNotShelter);
      });
    },

    getIsBaseStoreDK(state) {
      const config = useRuntimeConfig();
      return state.baseStore === (config.public.baseStoreDk || 'maxizooDK');
    },

    getIsBaseStoreFR(state) {
      const config = useRuntimeConfig();
      return state.baseStore === (config.public.baseStoreFr || 'maxizooFR');
    },

    getIsBaseStorePL(state) {
      const config = useRuntimeConfig();
      return state.baseStore === (config.public.baseStorePl || 'maxizooPL');
    },

    getProgressBarSteps(state) {
      return state.progress.steps;
    },

    getProgressBarActiveSteps(state) {
      return state.progress.activeStep;
    },

    shouldAppendUspToDescription(state) {
      return import.meta.server && ['product-listing', 'product-detail'].includes(state.pageTemplate);
    },

    getLocale(state: any): string {
      const { $i18n } = useNuxtApp();
      return $i18n.locales.value?.find((lang: any) => lang?.code === state.language)?.language || '';
    },

    getCountryCode() {
      // Casting to silence the only (weird) typescript error left in here
      return (this.getLocale as any)?.split('-')?.[1]?.toLowerCase() || '';
    },

    getProductRecalls(state: any) {
      const now = Date.now();
      const activeProductRecalls = state.productRecalls.filter((recall: ProductRecall) => {
        const endDate = new Date(recall.end_date ?? now).getTime();
        return endDate >= now;
      });

      return activeProductRecalls.sort(
        (a: ProductRecall, b: ProductRecall) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
    },
  },
  actions: {
    async setProductRecalls(nuxtApp: NuxtApp, page?: Record<string, any>) {
      const { $api, $i18n } = nuxtApp;

      let productRecalls = page?.content?.product_recalls;

      if (unref(useIsPageUrl('url.homepage'))) {
        const productRecallsPage = await $api.getPage($i18n.t('productRecalls.url'));
        productRecalls = productRecallsPage?.content?.product_recalls;
      }

      this.productRecalls = productRecalls ?? [];
    },

    async initConfiguration(nuxtApp: NuxtApp) {
      const datalayerStore = useDatalayerStore();

      logger.debug('Loading configuration...');
      const response = await nuxtApp.$api.hybris.configuration.global();
      this.configuration = response.data;

      // add conversion rate to datalayer from global config
      datalayerStore.conversionRateToEuro = this.configuration?.conversionRateToEuro;
    },

    async loadContentTiles(nuxtApp: NuxtApp) {
      logger.debug('Loading content tiles...');
      const response = await nuxtApp.$api.hybris.contentTiles.get('DEFAULT', this.language);
      this.contentTiles = response.data.map((tile: any) => JSON.parse(tile.content).fields);
    },

    async loadFeatureToggles(nuxtApp: NuxtApp) {
      logger.debug('Loading feature toggles...');
      const response = await nuxtApp.$api.hybris.configuration.features();
      this.features = response.data;
    },

    async loadHeader(nuxtApp: NuxtApp) {
      const { data } = await nuxtApp.$api.getHeader();
      this.header = data.content ?? {};
    },

    async loadFooter(nuxtApp: NuxtApp) {
      const { data } = await nuxtApp.$api.getFooter();
      this.footer = data.content ?? {};
    },

    /**
     * Returns the current page to be displayed.
     *
     * Fire and forget page loading function, used to retrieve page content from our CMS
     *
     * This method should only be used to retrieve and render the currently active page.
     * Do not use it to access or preload content from other pages that are not currently loaded.
     * @since: Nuxt3
     */
    async loadPage(requestedPageUrl?: string | Promise<string>) {
      const nuxtApp = useNuxtApp();

      const [requestedPageUrlValue] = await Promise.all([toValue(requestedPageUrl), nuxtApp.$router.isReady()]);

      if (!requestedPageUrlValue) {
        return this.loadedPage || false;
      }

      // String language prefix
      let pageUrl = removeLanguagePrefix(requestedPageUrlValue, nuxtApp);

      // Custom per-page URL mapping
      if (pageUrl === '/checkout/start/') {
        pageUrl = '/checkout/login';
      } else if (pageUrl.startsWith('/stores/') && pageUrl.split('/').length === 4) {
        pageUrl = '/storefinder/detail';
      } else if (pageUrl.startsWith('/p/') && pageUrl.split('/').length === 4) {
        pageUrl = '/p/product-detail';
      }

      this.loadedPage = await nuxtApp.$api.getPage(encodeURI(pageUrl));
      this.pageTemplate = this.loadedPage?.content?.template;

      return this.loadedPage || false;
    },

    /**
     * When used, this functions expects the route to define our custom page meta `routeCmsPage`.
     * Note: Type is not fully working in PHPStorm (?), but custom meta `routeCmsPage` is defined in our `index.d.ts`.
     * @param route Route object from inside the nuxt page setup. Calling `useRoute()` in this action does not work.
     */
    async loadRoutePage(route: RouteLocationNormalizedLoaded) {
      const nuxtApp = useNuxtApp();

      // Dev-only usage check, intended to be
      if (import.meta.dev) {
        const component = getCurrentInstance()?.type;

        try {
          const isRouteComponent =
            component && useRoute().matched.some((p) => Object.values(p.components || {}).includes(component));

          if (!isRouteComponent) {
            logger.error(
              // eslint-disable-next-line max-len
              `'mainStore.loadRoutePage()' should be called in nuxt page setup() with custom page meta 'routeCmsPage', are you sure this is correct?`,
            );
          }
        } catch {}
      }

      // During SSR, the page is supposed to be already loaded in the layout when this is called
      if (import.meta.server) {
        return this.loadedPage;
      }

      return nuxtApp.runWithContext(() => this.loadPage(route.meta.routeCmsPage?.(route, nuxtApp)));
    },

    setError(error: typeof this.error) {
      this.error = error;
    },

    clearError() {
      this.error = undefined;
    },

    startLoading() {
      this.isLoading = true;
    },

    finishLoading() {
      this.isLoading = false;
    },

    setProgressBarSteps(steps: any) {
      this.progress.steps = steps;
    },

    setProgressBarActiveSteps(steps: any) {
      this.progress.activeStep = steps;
    },

    updateDevicePlatform({ userAgent }: { userAgent?: string }) {
      if (!userAgent) {
        this.devicePlatform = null;
      } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
        this.devicePlatform = 'ios';
      } else if (/Android/i.test(userAgent)) {
        this.devicePlatform = 'android';
      } else {
        this.devicePlatform = null;
      }
    },

    setContactReasonExist(contactReasonExist = false) {
      this.contactReasonExist = contactReasonExist;
    },

    setAbTests(abTests: any) {
      this.abTests.tests = abTests ? JSON.stringify(abTests) : null;
    },

    async getTopCategories(tealiumCategories: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting personalized categories...');

      const categories: any[] = [];
      Object.keys(tealiumCategories).forEach((code, count) => {
        categories.push({ count, code });
      });

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.personalization
          .getTopCategories(categories, this.language)
          .then(async (response: any) => {
            logger.debug('Personalized categories load successful.');

            return resolve(response.data.categories);
          })
          .catch(async (error: any) => {
            logger.error('Error while getting personalized categories.', error);

            return reject(error);
          }),
      );
    },

    loadBillingCountries() {
      const nuxtApp = useNuxtApp();
      logger.debug('Loading billing countries...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.country
          .billing(this.language)
          .then((response: any) => {
            logger.debug('Billing countries loaded.');

            this.billingCountries = response.data.countries;

            return resolve(false);
          })
          .catch((error: any) => {
            logger.error('Error while loading billing countries.', error);

            return reject(error);
          }),
      );
    },

    loadShippingCountries() {
      const nuxtApp = useNuxtApp();
      logger.debug('Loading shipping countries...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.country
          .shipping(this.language)
          .then((response: any) => {
            logger.debug('Shipping countries loaded.');

            this.shippingCountries = response.data.countries;

            return resolve(undefined);
          })
          .catch((error: any) => {
            logger.error('Error while loading shipping countries.', error);

            return reject(error);
          }),
      );
    },

    uploadFile({ data, type }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Adding file...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.file
          .create(data, type)
          .then((response: any) => {
            logger.debug('File added.');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while adding file.', error);

            if (error.response) {
              nuxtApp.$errorHandler.handleErrorModal(error, false);
            }

            return reject(error);
          }),
      );
    },

    createShelterImage(data: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Adding image file...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.file
          .create(data, 'CUSTOMER')
          .then((response: any) => {
            logger.debug('Image added.');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while adding image file.', error);

            if (error.response) {
              nuxtApp.$errorHandler.handleErrorModal(error, false);
            }

            return reject(error);
          }),
      );
    },

    async loadCmsErrorPage() {
      const { $api } = useNuxtApp();
      logger.debug('Loading CMS Error-Page...');

      if (this.errorPage) {
        logger.debug('CMS Error-Page already loaded, returning from store.');
        return this.errorPage;
      }

      try {
        // Populate errorPage so that 4xx and 5xx pages can directly access it
        this.errorPage = await $api.getPage('error');
        logger.debug('Loaded CMS Error-Page.');
        return this.errorPage;
      } catch (error: any) {
        logger.error('Error while loading CMS Error-Page.', error);
        throw error;
      }
    },

    loadAccountNodes(nuxtApp: NuxtApp) {
      logger.debug('Loading account navigation nodes...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api
          .getNodes('/my-account/')
          .then((response: any) => {
            logger.debug('Account nodes loaded.');
            this.accountNodes = response.nodes || [];
            return resolve(this.accountNodes);
          })
          .catch((error: any) => {
            logger.error('Error while loading account navigation nodes.', error);

            return reject(error);
          }),
      );
    },

    getSearchTermSuggestions({ searchTerm }: { searchTerm: string | null }): Promise<Record<string, any>[]> {
      const nuxtApp = useNuxtApp();
      logger.debug(`Getting search term suggestions with SmartSearch enabled: ${this.abTests.searchhub}...`);

      let apiCall: any;
      if (searchTerm) {
        apiCall = nuxtApp.$api.hybris.product.suggestions(searchTerm, null, this.language, this.abTests);
      } else {
        apiCall = nuxtApp.$api.hybris.product.topSearchPhrases(this.language);
      }

      return new Promise((resolve, reject) =>
        apiCall
          .then((response: any) => {
            logger.debug('Got search term suggestions.');

            const data = response.data;
            return resolve(data.suggestions || data.topSearchPhrases);
          })
          .catch((error: any) => {
            logger.error('Error while getting search term suggestions.', error);

            return reject(error);
          }),
      );
    },

    async getProductSuggestions({ searchTerm }: { searchTerm: string | null }): Promise<Record<string, any>> {
      const nuxtApp = useNuxtApp();
      logger.debug(`Getting product suggestions with SmartSearch enabled: ${this.abTests.searchhub}...`);

      let searchIds: string[] | null = null;
      let abTests = { ...this.abTests } as Record<string, any> | null;

      if (!searchTerm) {
        const topSellers = await nuxtApp.$api.hybris.product.topSellers(5, this.language);
        searchIds = topSellers.data.map((product: Record<string, any>) => product.code).join(',');
        abTests = null;
      }

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.product
          .search(
            searchTerm,
            'relevance',
            0,
            null,
            searchIds,
            this.language,
            null,
            null,
            'DEFAULT',
            5,
            null,
            [],
            abTests,
          )
          .then((response: any) => {
            logger.debug('Got product suggestions.');

            const data = response.data;

            return resolve({
              products: data.products,
              categories: data.facets?.find((facet: Record<string, any>) => facet.code === 'category')?.nodes,
            });
          })
          .catch((error: any) => {
            logger.error('Error while getting product suggestions.');
            logger.error(error);

            return reject(error);
          }),
      );
    },

    saveContactForm({ formId, fields }: { formId: string; fields: any }) {
      const nuxtApp = useNuxtApp();
      logger.debug('Saving contact form...');

      const { recaptchaResponse } = fields;

      // remove ReCaptcha from fieldValues - for nuxtApp OCC expected outside the object
      delete fields.recaptchaResponse;
      delete fields.validateRecaptcha;

      const entry: any[] = [];
      Object.entries(fields).forEach(([key, value]) => {
        entry.push({ key, value });
      });

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.contactForm
          .save(formId, entry, recaptchaResponse, this.language)
          .then((response: any) => {
            logger.debug('Contact form saved.');

            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while saving contact form.', error);

            return reject(error);
          }),
      );
    },

    saveContactFileForm({ formId, fields }: { formId: string | number; fields: any }) {
      const nuxtApp = useNuxtApp();
      // @ts-ignore
      const { getType: userType } = useUserStore();
      logger.debug('Saving contact form for authenticated user...');

      const { files } = fields;
      delete fields.files;

      const dtoString = {
        formId,
        fieldValues: {
          ...fields,
        },
        recaptchaResponse: fields.recaptchaResponse ?? null,
      };

      // remove ReCaptcha from fieldValues - for this OCC expected outside the object
      delete dtoString.fieldValues.recaptchaResponse;

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.contactForm
          .saveWithFiles(userType, dtoString, files, this.language)
          .then((response: any) => {
            logger.debug('Contact form with files saved.');

            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while saving contact form with files.', error);

            return reject(error);
          }),
      );
    },

    registerRaffleEntry(payload: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Register raffle entry...');
      // @ts-ignore
      const { getType: userType } = useUserStore();
      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.raffle
          .register(userType, payload)
          .then((response: any) => {
            logger.debug('Registered raffle entry...');
            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while registering raffle entry.', error);

            return reject(error);
          }),
      );
    },

    getSeoStoreList(): Promise<Record<string, any>[]> {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting SEO stores...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.store
          .getSeoStores(this.language)
          .then((response: any) => {
            logger.debug('Got SEO stores.');

            return resolve(response.data.stores);
          })
          .catch((error: any) => {
            logger.error('Error while getting SEO stores.', error);

            return reject(error);
          }),
      );
    },

    getRegionsStoresList() {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting stores grouped by region...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.store
          .getRegionsStores(this.language)
          .then((response: any) => {
            logger.debug('Got region stores.');

            return resolve(response.data.regionStores);
          })
          .catch((error: any) => {
            logger.error('Error while getting region stores.', error);

            return reject(error);
          }),
      );
    },

    async getStoreList({ query, latitude, longitude, currentPage, onlySalons, radius }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting stores...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.store
          .all(query, latitude, longitude, currentPage, this.language, onlySalons, radius)
          .then((response: any) => {
            logger.debug('Got stores.');
            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while getting stores.', error);
            return reject(error);
          }),
      );
    },

    getStoreListSorted({ query, latitude, longitude, currentPage, onlySalons, radius, sort, productId }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting stores...');
      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.store
          .getProductAvailability(
            query,
            latitude,
            longitude,
            currentPage,
            this.language,
            onlySalons,
            radius,
            sort,
            productId,
          )
          .then((response: any) => {
            logger.debug('Got stores.');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while getting stores.', error);

            return reject(error);
          }),
      );
    },

    getStoreListFiltered({ query, latitude, longitude, radius, filterProperties }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting filtered stores...');
      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.store
          .allFiltered(query, latitude, longitude, this.language, radius, filterProperties)
          .then((response: any) => {
            logger.debug('Got filtered stores.');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while getting filtered stores.', error);

            return reject(error);
          }),
      );
    },

    async getStoreLocation(location: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Find stores by location...');

      try {
        this.searchedStores = (await nuxtApp.$api.hybris.store.getStoreLocation(location)).data;
        logger.debug('Stores found...');
      } catch (error: any) {
        logger.error('Error while searching for stores', error);
        throw error;
      }
    },

    getProductAvailabilityInStore({ productId, storeId }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting stores...');
      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.product
          .productAvailabilityInStore(productId, this.language, storeId)
          .then((response: any) => {
            logger.debug('Got stores.');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while getting stores.', error);

            return reject(error);
          }),
      );
    },

    deleteFile(mediaId: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Delete file...');

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.file
          .delete(mediaId)
          .then((response: any) => {
            logger.debug('File deleted.');

            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while deleting file.', error);

            if (error.response) {
              nuxtApp.$errorHandler.handleErrorModal(error, false);
            }

            return reject(error);
          }),
      );
    },

    async loadStoresSelectables() {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting all stores selectable values...');
      const userStore = useUserStore();

      try {
        const response = await nuxtApp.$api.hybris.store.getStoresSelectables();
        userStore.storesSelectables = response.data;
      } catch (error) {
        logger.error('Error while searching for stores', error);
      }
    },

    bookReservation({ payload, serviceProviderType }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Sending booking request...');
      // @ts-ignore
      const { getType: userType } = useUserStore();

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.booking
          .bookReservation(userType, serviceProviderType, payload)
          .then(async (response: any) => {
            logger.debug('Booking request accepted.');

            return resolve(response);
          })
          .catch(async (error: any) => {
            logger.error('Error while booking.', error);

            nuxtApp.$errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },

    getAvailableTimeSlots({ payload, serviceProviderType }: any): Promise<any> {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting timeslots...');
      // @ts-ignore
      const { getType: userType } = useUserStore();
      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.booking
          .getAvailableTimeslots(userType, serviceProviderType, payload)
          .then(async (response: any) => {
            logger.debug('Received available time slots.');
            return resolve(response);
          })
          .catch(async (error: any) => {
            logger.error('Error while getting time slots.', error);

            nuxtApp.$errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },

    getTreatments({ serviceProviderType }: any) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Getting treatments...');
      const userType = useUserStore().getType;
      return new Promise((resolve, reject) =>
        $api.hybris.booking
          .getTreatments(userType, serviceProviderType)
          .then(async (response: any) => {
            logger.debug('Received available treatments.');
            return resolve(response);
          })
          .catch(async (error: any) => {
            logger.error('Error while getting treatments.');
            logger.error(error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },

    getStoreById({ id }: any): Promise<any> {
      const { $api } = useNuxtApp();
      logger.debug('Getting store by id...');
      return new Promise((resolve, reject) =>
        $api.hybris.store
          .find(id)
          .then((response: any) => {
            logger.debug('Got store.');
            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while getting store by id.');
            logger.error(error);

            return reject(error);
          }),
      );
    },

    setHeaderClosed(headerType: string) {
      if (headerType in this) {
        // @ts-ignore
        this[headerType] = true;
      }
    },

    closeHeader({ headerType, days = 0 }: any) {
      const key = `${headerType}Closed`;

      // Update state to make closing reactive
      this.setHeaderClosed(key);

      if (days > 0) {
        // Update cookie to persist closing
        const cookie = useCookie(key, {
          expires: addDaysToNow(days),
        });
        cookie.value = '1';
      }
    },

    cancelReservation({ reservationId, serviceProviderType }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Sending cancellation booking request...');
      // @ts-ignore
      const { getType: userType } = useUserStore();

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.booking
          .cancelReservation(userType, serviceProviderType, reservationId)
          .then(async (response: any) => {
            logger.debug('Booking cancellation successful.');

            return resolve(response);
          })
          .catch(async (error: any) => {
            logger.error('Error while cancelling booking.', error);

            nuxtApp.$errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },

    getSheltersList({ currentPage, pageSize }: any) {
      const nuxtApp = useNuxtApp();
      logger.debug('Getting shelters list...');
      const lang = this.language;

      return new Promise((resolve, reject) =>
        nuxtApp.$api.hybris.shelters
          .all(currentPage, pageSize, lang)
          .then(async (response: any) => {
            logger.debug('Shelters list load successful.');

            return resolve(response.data);
          })
          .catch(async (error: any) => {
            logger.error('Error while getting shelters list.', error);

            return reject(error);
          }),
      );
    },

    async init(nuxtApp: NuxtApp): Promise<void> {
      const userStore = useUserStore(nuxtApp.$pinia);
      const wishlistStore = useWishlistStore(nuxtApp.$pinia);
      const datalayerStore = useDatalayerStore(nuxtApp.$pinia);

      // Step #1: Queue requests which doesn't require authentication
      const requests = [
        this.loadFeatureToggles(nuxtApp),
        this.loadHeader(nuxtApp),
        this.loadFooter(nuxtApp),
        this.loadContentTiles(nuxtApp),
        this.loadAccountNodes(nuxtApp),
      ];

      // Step #2: Queue requests which require authentication
      if (userStore.getIsAuthenticated) {
        requests.push(
          nuxtApp.runWithContext(() =>
            userStore.loadUser({
              // Soft-login state is only relevant for privacy-related actions, e.g. open my-account or checkout where
              // you can see user information like addresses. However, on the initial page load we don't want to trigger
              // the modal because it should only annoy the user after a critical action.
              preventSoftLoginModal: true,
              analyticsResourceList: [resourceNames.USER, resourceNames.CART, resourceNames.WISHLIST],
            }),
          ),
        );

        requests.push(nuxtApp.runWithContext(() => wishlistStore.initializeWishlist()));
      } else {
        // This is the "fallback" for when we don't automatically load analytics data via loadUser
        requests.push(datalayerStore.loadAnalytics(nuxtApp));
      }

      // Step #3: Now we wait for the all requests and check for possible errors
      const error = (await Promise.allSettled(requests)).find(
        (p: PromiseSettledResult<any>) => p.status === 'rejected',
      )?.reason;

      if (error) {
        // Soft login
        if (isResponseError(error, 401, REQUIRES_HARD_LOGIN_ERROR)) {
          userStore.softLogin = true;
          userStore.setNameFromCookie();
          return;
        }

        // Invalid token
        if (isResponseError(error, 401, INVALID_TOKEN_ERROR)) {
          await nuxtApp.runWithContext(() => this.resetLogout({ invalidToken: true, reason: 'Invalid token' }));
          return this.init(nuxtApp);
        }

        // Maintenance mode
        if (isResponseError(error, 503)) {
          await nuxtApp.runWithContext(() => navigateTo(`https://${nuxtApp.$domain}/maintenance/`));
          return;
        }

        throw createError({
          message: error.message || error.reason || error.type,
          statusCode: 500,
          fatal: true,
          stack: error.stack,
        });
      }

      this.updateDefaultCountryInStore();
    },

    /**
     * @param invalidToken Skips logout API call when defined, because why invalidate an already invalid token?
     * @param reason Additional reset reason which is added to our debug logs
     */
    async resetLogout(
      { invalidToken = false, reason }: { invalidToken: boolean; reason?: string } = { invalidToken: false },
    ) {
      const { $appBridge } = useNuxtApp();
      const userStore = useUserStore();
      const wishlistStore = useWishlistStore();
      const datalayerStore = useDatalayerStore();
      const cartStore = useCartStore();
      const newsletterStore = useNewsletterStore();
      const orderStore = useOrderStore();

      logger.debug(`Resetting application... ${reason ? ` (${reason})` : ''}`);

      try {
        await userStore.logout(invalidToken);
      } catch (error) {
        logger.error('Error during token revocation', error);
        throw showError(
          new Error(`Unrecoverable error while revoking token:\n\n${error?.toString()}`, { cause: error }),
        );
      }

      // Reset stores
      datalayerStore.cart = null;
      datalayerStore.bvInformation = null;
      datalayerStore.user = null;
      datalayerStore.wishlist = null;
      newsletterStore.$reset();
      wishlistStore.$reset();
      cartStore.$reset();
      userStore.$reset();
      orderStore.$reset();

      // Needs to be called to trigger a watcher that updates all dependencies (Store, Cookie, AppBridge, API, etc.)
      userStore.setToken(null);

      useDeleteCookies([
        'appview',
        'cart',
        'cart-payment',
        'campaignHeaderClosed',
        'cclattr',
        'FN-UX',
        'friendsBannerClosed',
        'no-adobe',
        'smartbanner_exited',
        'token',
        'uid',
      ]);

      deleteAdyenPayload();
      deleteFromLocalStorage(LOCAL_STORAGE_ADYEN_ACTION);
      deleteFromLocalStorage(LOCAL_STORAGE_DELIVERY_OPTION);
      deleteFromLocalStorage(LOCAL_STORAGE_PICKUP_DELIVERY_ADDRESS);

      $appBridge.actions.logout();

      // Clear sentry user
      getCurrentScope().setUser(null);

      logger.debug('Finished resetting application.');

      return true;
    },

    updateDefaultCountryInStore() {
      this.defaultCountry =
        this.baseStoreConfig.defaultCountry || this.baseStore?.slice(-2) || this.language.toUpperCase();
    },

    clearFlexibleLayoutData() {
      this.flexibleHeader = null;
      this.flexibleFooter = null;
      this.flexibleLayoutType = null;
    },

    // Used to silence TS error for mapActions
    reset() {
      this.$reset();
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMainStore, import.meta.hot));
}
